import { http } from '@pendo/http';

/**
 * @typedef {object} User
 * @property {string} id
 * @property {string} username
 * @property {string} first
 * @property {string} last
 * @property {number} role
 * @property {string} userType
 * @property {boolean} hasLoggedIn
 * @property {number} lastLogin Timestamp
 * @property {string} useCase
 * @property {string} jobRole
 */

/**
 * @typedef {object} VersionInfo
 * @property {number} version
 * @property {number} createdAt Timestamp
 * @property {number} lastUpdatedAt Timestamp
 * @property {User} createdByUser
 * @property {User} lastUpdatedByUser
 */

/**
 * @typedef {object} ExperimentVariant
 * @property {string} name
 * @property {string} guideId
 * @property {number} groupSize
 */

/**
 * @typedef {object} ExperimentConversion
 * @property {string} itemType e.g., page, feature, track event
 * @property {string} itemId
 * @property {number} attributionWindow Milliseconds
 */

/**
 * @typedef {object} Experiment
 * @property {string} id
 * @property {number} appId
 * @property {string} name
 * @property {string} description
 * @property {'Draft' | 'Active'} state
 * @property {string} audience
 * @property {string} audienceUiHint
 * @property {ExperimentVariant[]} variants
 * @property {ExperimentConversion} conversion
 * @property {number} startTime Timestamp
 * @property {number} endTime Timestamp
 * @property {number} version
 * @property {number} createdAt Timestamp
 * @property {number} lastUpdatedAt Timestamp
 * @property {User} createdByUser
 * @property {User} lastUpdatedByUser
 */

/**
 * @typedef {object} ExperimentVariantInput
 * @property {string} [name]
 * @property {string} [guideId]
 * @property {number} [groupSize]
 */

/**
 * @typedef {object} ExperimentConversionInput
 * @property {string} [itemType]
 * @property {string} [itemId]
 * @property {number} [attributionWindow] Milliseconds
 */

/**
 * Input object for creating or updating an experiment.
 * @typedef {object} ExperimentInput
 * @property {number} [appId]
 * @property {string} [name]
 * @property {string} [description]
 * @property {'Draft' | 'Active' } [state] // Update this as we add statuses.
 * @property {string} [audience]
 * @property {string} [audienceUiHint]
 * @property {ExperimentVariantInput[]} [variants]
 * @property {ExperimentConversionInput} [conversion]
 * @property {number} [startTime] Timestamp
 * @property {number} [endTime] Timestamp
 */

/**
 * Service for managing Guide Experiments API
 */
export class ExperimentsService {
    /**
     * Get all experiments
     * @returns {Promise<Experiment[]>} A promise that resolves to the list of experiments
     */
    static getExperiments () {
        return http.get('/api/s/_SID_/guideexperiment');
    }

    /**
     * Get a specific experiment by ID
     * @param {string} id - The experiment ID
     * @returns {Promise<Experiment>} A promise that resolves to the experiment data
     */
    static getExperiment (id) {
        return http.get(`/api/s/_SID_/guideexperiment/${id}`);
    }

    /**
     * Create a new experiment
     * @param {ExperimentInput} experiment - The experiment data
     * @returns {Promise<Experiment>} A promise that resolves to the created experiment
     */
    static createExperiment (experiment) {
        return http.post('/api/s/_SID_/guideexperiment', experiment);
    }

    /**
     * Update an existing experiment
     * @param {string} id - The experiment ID
     * @param {ExperimentInput} experiment - The experiment data to update
     * @returns {Promise<Experiment>} A promise that resolves to the updated experiment
     */
    static updateExperiment (id, experiment) {
        return http.put(`/api/s/_SID_/guideexperiment/${id}`, experiment);
    }

    /**
     * Delete an experiment
     * @param {string} id - The experiment ID
     * @returns {Promise<void>} A promise that resolves when the experiment is deleted
     */
    static deleteExperiment (id) {
        return http.delete(`/api/s/_SID_/guideexperiment/${id}`);
    }
}
